import { useLocation } from 'react-router-dom';

import { useActivation } from '../ActivationContext';

import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { REDIRECT_TO_SESSION_KEY, CANOPY_POST_AUTH_CALLBACK } from '@/utils/constants';

/**
 * Where to send the member's device once authentication finishes.
 *
 * **Why this is looked up rather than held in the flow's state.** The app opens the
 * portal inside a browser and watches for it to navigate to a private address carrying a
 * one-time code. If that address is lost anywhere along the way, the member completes
 * every step successfully and the app never finds out — they are left looking at a
 * browser that goes nowhere.
 *
 * The flow's in-memory state is not a reliable place to keep it. A member who arrives by
 * following a link from their email lands in a **fresh browser tab**, so the state built
 * up before the email was sent is gone, and the address with it. The screens after email
 * verification run in exactly that case.
 *
 * So the address is looked for in three places, most specific first: this screen's own
 * URL, the copy saved when the member first arrived, and whatever the flow's state still
 * holds. Every candidate is checked before it is trusted, because an unvalidated redirect
 * target is an open redirect.
 *
 * There is one lookup here, in two forms, differing only in what happens when none of the
 * three places has an address:
 *
 * - `appSuppliedRedirectTo` returns nothing. Use it when relaying the address onward to
 *   the auth service, where absent has to stay absent.
 * - `resolvePostAuthRedirectTo` returns the app's single post-authentication address. Use
 *   it at the end of the flow, where some address has to be chosen.
 *
 * **That last step is a default, not a decision.** An earlier version chose between two
 * addresses depending on whether it believed the member was new or returning. Guess wrong
 * and a returning member was sent to a screen that needs a credential it was never given,
 * landing them back on sign-up (PAT-1286). There is now one address, so there is nothing
 * to guess — and no intent to guess it from.
 */

interface RedirectToSources {
  /** The current screen's query string, e.g. `useLocation().search`. */
  search: string;
  /** Whatever the activation flow's state still holds, when a caller has that state. */
  fromFlowState?: string | null;
}

/**
 * The address the app actually asked for, or nothing at all.
 *
 * Returning nothing matters when we relay this to the auth service: claiming an app
 * address for a member who is signing in on a desktop browser would put a private app
 * link in their email that their machine cannot open. Absent means absent.
 */
export const appSuppliedRedirectTo = (
  { search, fromFlowState }: RedirectToSources,
): string | undefined => {
  const fromUrl = new URLSearchParams(search).get('redirectTo');
  if (fromUrl && isSafeRedirectUri(fromUrl)) { return fromUrl; }

  // Guarded because this also runs in a render path. A browser with storage locked down
  // throws on access, and there is no error boundary in this app — an unguarded read here
  // blanks the screen mid-flow. Treat unreadable storage as "no saved value" and fall
  // through to the remaining candidates.
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
  } catch {
    stored = null;
  }
  if (stored && isSafeRedirectUri(stored)) { return stored; }

  if (fromFlowState && isSafeRedirectUri(fromFlowState)) { return fromFlowState; }

  return undefined;
};

/**
 * The same lookup, with the app's single post-authentication address as the answer when
 * nothing was supplied. Kept free of React context so the callbacks that run outside the
 * activation provider resolve the address exactly the same way as the screens inside it.
 */
export const resolvePostAuthRedirectTo = (
  sources: RedirectToSources,
): string => appSuppliedRedirectTo(sources) ?? CANOPY_POST_AUTH_CALLBACK;

/**
 * The hook form, for screens mounted inside the activation provider. It supplies the two
 * inputs the lookup needs — this screen's query string and the flow's state — and returns
 * the resolved address.
 */
export const usePostAuthRedirectTo = (): string => {
  const { search } = useLocation();
  const { state } = useActivation();

  return resolvePostAuthRedirectTo({ search, fromFlowState: state.redirectTo });
};

/**
 * The hook form of the relay lookup, for screens inside the provider that need to pass
 * the address to the auth service rather than navigate to it.
 */
export const useAppSuppliedRedirectTo = (): string | undefined => {
  const { search } = useLocation();
  const { state } = useActivation();

  return appSuppliedRedirectTo({ search, fromFlowState: state.redirectTo });
};
